import { useEffect, useRef, useState } from 'react';
import type { Scope } from '../scopes';
import { labelForScope, SCOPES } from '../scopes';
import type { Task, TaskPriority, TaskStatus } from '../types';
import { priorityLabel } from '../utils/priority';
import { getAttachmentUrl } from '../utils/apiClient';
import { useTeam } from '../context/TeamContext';
import { useAuth } from '../context/AuthContext';
import { AttachmentPill } from './AttachmentPill';
import { NotesTimeline } from './NotesTimeline';

/** Strip `/<handle> …` directive line from already-ingested bodies. */
function stripDirectiveLine(body: string | undefined): string | undefined {
  if (!body) return body;
  return body.replace(/^\s*\/\w+\b[^\r\n]*\r?\n+/i, '').trimStart();
}

/**
 * Cut legacy body text at the first "From:" forward marker so the user's
 * reply + signature doesn't leak into the email viewer for tasks that were
 * ingested before the ingest-side fix.
 */
function cutTextAtForwardMarker(body: string | undefined): string | undefined {
  if (!body) return body;
  const markers: RegExp[] = [
    /^-+\s*Forwarded message\s*-+$/im,
    /^Begin forwarded message:/im,
    /^>+\s*From:/m,
    /^From:[ \t].+[\r\n]+(?:Sent|To|Subject|Date):[ \t]/im,
    /^From:[ \t].+[\s\S]{0,800}?^Subject:[ \t]/im,
  ];
  for (const m of markers) {
    const match = m.exec(body);
    if (match && match.index > 0) {
      return body.slice(match.index).trim();
    }
  }
  return body;
}

/** HTML variant of cutTextAtForwardMarker — slices at first "From:" marker in HTML source. */
function cutHtmlAtForwardMarker(html: string | undefined): string | undefined {
  if (!html) return html;
  const marker = /(?:^|>)\s*(?:<[^>]*>\s*)*From\s*:/i;
  const match = marker.exec(html);
  if (!match) return html;
  const startOfTag = html.lastIndexOf('<', match.index);
  const cutAt = startOfTag >= 0 ? startOfTag : match.index;
  return html.slice(cutAt);
}

interface Props {
  task: Task;
  onUpdate: (patch: {
    title?: string;
    description?: string;
    priority?: TaskPriority;
    pinned?: boolean;
    dueDate?: string | null;
    status?: TaskStatus;
    assigneeId?: string;
    private?: boolean;
  }) => Promise<void>;
  onMove: (toScope: Scope) => Promise<void>;
  onDelete: () => void | Promise<void>;
  onAddNote: (body: string) => Promise<void>;
  onBack?: () => void;
}

const EMAIL_IFRAME_STYLE = `
<meta name="viewport" content="width=device-width, initial-scale=1">
<base target="_blank">
<style>
  html, body {
    margin: 0;
    padding: 14px 16px;
    background: #f1f5f9;
    color: #111827;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    word-wrap: break-word;
    overflow-wrap: anywhere;
    overflow-x: auto;
  }
  a { color: #0ea5c9; }
  /* Force-shrink email-marketing templates that set fixed widths via HTML attrs.
     !important beats <table width="600"> etc. */
  img, video {
    max-width: 100% !important;
    height: auto !important;
    width: auto !important;
    display: block;
  }
  table {
    max-width: 100% !important;
    width: auto !important;
    table-layout: auto;
  }
  td, th {
    max-width: 100% !important;
    word-break: break-word;
  }
  pre, code {
    max-width: 100%;
    white-space: pre-wrap;
    word-break: break-word;
  }
  blockquote { border-left: 3px solid #cbd5e1; padding-left: 12px; color: #475569; margin: 8px 0; }
</style>
`;

function safeSrcDoc(html: string | undefined): string {
  if (!html) return '';
  return EMAIL_IFRAME_STYLE + html;
}

export function TaskDetail({ task, onUpdate, onMove, onDelete, onAddNote, onBack }: Props) {
  const [titleDraft, setTitleDraft] = useState(task.title);
  const [downloadingName, setDownloadingName] = useState<string | null>(null);
  const fieldsRef = useRef<HTMLDetailsElement>(null);

  // Default the details disclosure open on desktop, closed on mobile.
  useEffect(() => {
    if (!fieldsRef.current) return;
    const isMobile = window.matchMedia('(max-width: 900px)').matches;
    fieldsRef.current.open = !isMobile;
  }, []);
  const { members } = useTeam();
  const { user } = useAuth();
  const canMakePrivate = !task.assigneeId || !task.createdBy || task.assigneeId === task.createdBy;
  const isCreator = !!user && task.createdBy === user.userId;

  async function downloadAttachment(filename: string) {
    setDownloadingName(filename);
    try {
      const url = await getAttachmentUrl(task.scope, task.taskId, filename);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setDownloadingName(null);
    }
  }

  async function commitTitle() {
    const next = titleDraft.trim();
    if (!next || next === task.title) {
      setTitleDraft(task.title);
      return;
    }
    await onUpdate({ title: next });
  }

  return (
    <div className="task-detail">
      <header className="task-detail-header">
        {onBack && (
          <button
            type="button"
            className="task-detail-back"
            onClick={onBack}
            aria-label="Back to list"
          >
            ←
          </button>
        )}
        <input
          className="task-detail-title"
          value={titleDraft}
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={() => void commitTitle()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
        />
        <div className="task-detail-actions">
          <button
            aria-pressed={task.pinned}
            onClick={() => void onUpdate({ pinned: !task.pinned })}
          >
            {task.pinned ? 'Unpin' : 'Pin'}
          </button>
          {task.status !== 'completed' ? (
            <button
              className="primary"
              onClick={() => void onUpdate({ status: 'completed' })}
            >
              Complete
            </button>
          ) : (
            <button onClick={() => void onUpdate({ status: 'open' })}>
              Reopen
            </button>
          )}
          <button className="danger" onClick={() => void onDelete()}>
            Delete
          </button>
        </div>
      </header>

      <details className="task-detail-fields-wrap" ref={fieldsRef}>
        <summary>
          <span className="task-detail-fields-summary-label">Details</span>
          <span className="task-detail-fields-summary-chips">
            <span>{priorityLabel(task.priority)}</span>
            <span>·</span>
            <span>{task.dueDate ? new Date(task.dueDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : 'No due date'}</span>
            <span>·</span>
            <span>{labelForScope(task.scope)}</span>
            <span>·</span>
            <span>{task.status === 'open' ? 'Open' : task.status === 'pending' ? 'Pending' : 'Completed'}</span>
          </span>
        </summary>
        <div className="task-detail-fields">
        <label>
          <span>Priority</span>
          <select
            value={task.priority}
            onChange={(e) =>
              void onUpdate({ priority: e.target.value as TaskPriority })
            }
          >
            {(['low', 'medium', 'high'] as TaskPriority[]).map((p) => (
              <option key={p} value={p}>
                {priorityLabel(p)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Due date</span>
          <input
            type="date"
            value={task.dueDate ? task.dueDate.slice(0, 10) : ''}
            onChange={(e) =>
              void onUpdate({ dueDate: e.target.value || null })
            }
          />
        </label>
        <label>
          <span>Scope</span>
          <select
            value={task.scope}
            onChange={(e) => void onMove(e.target.value as Scope)}
          >
            {SCOPES.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Status</span>
          <select
            value={task.status}
            onChange={(e) =>
              void onUpdate({ status: e.target.value as TaskStatus })
            }
          >
            <option value="open">Open</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
          </select>
        </label>
        {members.length > 1 && (
          <label>
            <span>Assignee</span>
            <select
              value={task.assigneeId ?? ''}
              disabled={task.private}
              onChange={(e) => void onUpdate({ assigneeId: e.target.value })}
              title={task.private ? 'Private tasks are assigned to their creator' : undefined}
            >
              {members.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.displayName}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="task-private-label">
          <span>Private</span>
          <input
            type="checkbox"
            checked={task.private === true}
            disabled={!canMakePrivate || !isCreator}
            onChange={(e) => void onUpdate({ private: e.target.checked })}
            title={
              !isCreator
                ? 'Only the creator can toggle privacy'
                : !canMakePrivate
                ? 'Reassign back to yourself first'
                : task.private
                ? 'Make shared — everyone in your team will see this'
                : 'Make private — only you will see this'
            }
          />
        </label>
        </div>
      </details>

      {task.directiveWarning && (
        <div className="directive-warning" role="status">
          ⚠ {task.directiveWarning}
        </div>
      )}

      {task.source && (
        <section className="email-source">
          {task.source.forwardNote && (
            <div className="forward-note">
              <strong>Your note:</strong> {task.source.forwardNote}
            </div>
          )}
          <div className="email-meta">
            {task.source.originalFrom && (
              <div>
                <strong>From:</strong> {task.source.originalFrom}
              </div>
            )}
            {task.source.originalDate && (
              <div>
                <strong>Date:</strong>{' '}
                {new Date(task.source.originalDate).toLocaleString()}
              </div>
            )}
            {task.source.originalSubject && (
              <div>
                <strong>Subject:</strong> {task.source.originalSubject}
              </div>
            )}
          </div>
          {task.source.attachments && task.source.attachments.length > 0 && (
            <AttachmentPill
              attachments={task.source.attachments}
              onDownload={downloadAttachment}
              downloadingName={downloadingName}
            />
          )}
          {task.source.originalBodyHtml ? (
            <iframe
              className="email-body"
              title="Original email"
              sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              srcDoc={safeSrcDoc(cutHtmlAtForwardMarker(task.source.originalBodyHtml))}
            />
          ) : task.source.originalBodyText ? (
            <pre className="email-body-text">
              {cutTextAtForwardMarker(stripDirectiveLine(task.source.originalBodyText))}
            </pre>
          ) : null}
        </section>
      )}

      {task.description && !task.source && (
        <section className="task-description">
          <p>{task.description}</p>
        </section>
      )}

      <footer className="task-detail-footer">
        <small className="muted">In {labelForScope(task.scope)}</small>
        <small className="muted">
          Updated {new Date(task.updatedAt).toLocaleString()}
        </small>
      </footer>

      <NotesTimeline notes={task.notes ?? []} onAdd={onAddNote} />
    </div>
  );
}
